import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Upload, Loader2, X, Edit2, Trash2, FileImage, Save,
  Home, ChevronLeft, ChevronRight, Tag, CheckCircle2, AlertTriangle, PenTool, ArrowLeft,
  Layers, Eraser, PenLine, RefreshCcw,
} from 'lucide-react';
import TraceTemplateModal from '@/components/TraceTemplateModal';
import { TemplateUsageList } from '@/components/trace/TemplateUsageList';
import RoofDiagramPicker from '@/components/RoofDiagramPicker';
import { toast } from 'sonner';

/**
 * Roof Design Templates library — visual catalogue of stylised roof plan
 * diagrams (4 Hipper, 5 Hip 1 Valley, Dog Leg variants, etc.).
 *
 * Admin features: drag-drop upload (.doc / .docx / .png / .jpg / .gif),
 * edit name/notes/counts, delete. Non-admins get read-only browsing for
 * use as a reference + the picker on Appraisal Roof tabs.
 */
export default function RoofTemplatesPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Iter180 — when this page is opened from the property's "Next:
  // Choose Roof Template" CTA we pin the screenshot + property context
  // at the top so the operator can compare templates side-by-side.
  const ctxPropertyId       = searchParams.get('property_id') || '';
  const ctxScreenshotPhotoId = searchParams.get('screenshot_photo_id') || '';
  const ctxAddress          = searchParams.get('address') || '';
  // iter303ai-fix12 · When this page is opened from the Workflow Runner
  // (Action 5 `roof_template_pick`), the URL includes `return_to=<path>`.
  // On Accept + on Back button, we honour that instead of dumping the
  // operator on the full Properties page. Defensively URL-decoded and
  // constrained to same-origin paths (must start with '/').
  const rawReturnTo = searchParams.get('return_to') || '';
  const ctxReturnTo = (rawReturnTo && rawReturnTo.startsWith('/')) ? rawReturnTo : '';
  // Iter180 Phase 3 — `activeScreenshotId` follows the URL param by
  // default but is mutable: the operator can swap to a different
  // screenshot via the "Change screenshot" picker without leaving
  // the templates page.
  const [activeScreenshotId, setActiveScreenshotId] = useState(ctxScreenshotPhotoId);
  useEffect(() => { setActiveScreenshotId(ctxScreenshotPhotoId); }, [ctxScreenshotPhotoId]);
  const [ctxPhotoUrl, setCtxPhotoUrl] = useState('');
  const [ctxPhoto, setCtxPhoto] = useState(null);
  useEffect(() => {
    if (!activeScreenshotId) { setCtxPhotoUrl(''); setCtxPhoto(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/photos/by-id/${activeScreenshotId}`);
        setCtxPhotoUrl(data?.url || '');
        setCtxPhoto(data || null);
      } catch { setCtxPhotoUrl(''); setCtxPhoto(null); }
    })();
  }, [api, activeScreenshotId]);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  // Iter180 Phase 2 — compare-mode bits. When the page is opened from
  // a property's Roof Screenshot, we render a sticky left-pane with
  // the screenshot and ghost-overlay whichever template the operator
  // clicks on the right (instead of opening the detail modal).
  const compareMode = !!(ctxPropertyId && ctxScreenshotPhotoId);
  const [overlay, setOverlay] = useState(null);   // currently-overlayed template object
  const [overlayOpacity, setOverlayOpacity] = useState(0.55);
  // Iter180 Phase 3 — "Change screenshot" picker visibility.
  const [screenshotPickerOpen, setScreenshotPickerOpen] = useState(false);
  // Iter180 Phase 3 — "Accept as Main Roof Template" persistence flag
  // so the button can briefly show a spinner and lock during the PATCH.
  const [accepting, setAccepting] = useState(false);
  const [acceptedId, setAcceptedId] = useState('');

  // Load the current property to surface the previously-accepted
  // template (if any) so the operator sees an "Already accepted" hint
  // and doesn't accidentally re-accept the same one.
  useEffect(() => {
    if (!compareMode) { setAcceptedId(''); return; }
    (async () => {
      try {
        const { data } = await api.get(`/properties/${ctxPropertyId}`);
        setAcceptedId(data?.roof_template_id || '');
      } catch { setAcceptedId(''); }
    })();
  }, [api, compareMode, ctxPropertyId]);

  const acceptOverlayAsMain = async (tplArg = null) => {
    const tpl = tplArg || overlay;
    if (!tpl?._id || !ctxPropertyId) return;
    setAccepting(true);
    try {
      await api.patch(`/properties/${ctxPropertyId}`, { roof_template_id: tpl._id });
      setAcceptedId(tpl._id);
      setOverlay(tpl);
      toast.success(`Accepted "${tpl.name}" as the main Roof Template for ${ctxAddress || 'this property'}.`);
      // iter303ai-fix12 · Runner-context handoff. If we were invoked from
      // the Workflow Runner (Action 5 `roof_template_pick` includes a
      // `return_to=/workflow/{runId}` query param), navigate back to the
      // runner so the operator resumes right where they left off.
      // Otherwise fall back to the property page (legacy behaviour).
      const returnUrl = ctxReturnTo || `/properties/${ctxPropertyId}`;
      setTimeout(() => navigate(returnUrl), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to accept template');
    } finally {
      setAccepting(false);
    }
  };
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadFlash, setUploadFlash] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(null);
  // Standalone trace tool — opens an aerial-picker overlay then the
  // TraceTemplateModal so the operator can build a new line-art template
  // from any aerial in the library (not tied to a specific appraisal).
  const [tracePickerOpen, setTracePickerOpen] = useState(false);
  const [traceAerials, setTraceAerials]       = useState([]);
  const [traceAerialsLoading, setTraceAerialsLoading] = useState(false);
  const [tracingAerial, setTracingAerial]     = useState(null);
  // Iter182++ — sort + recycle-bin state. `sortBy` mirrors the
  // backend whitelist; `viewMode='recycle'` flips the list to
  // ?only_deleted=true and exposes Restore / Permanently-delete actions.
  const [sortBy, setSortBy] = useState('name');   // name | created_at | category
  const [sortDir, setSortDir] = useState('asc');  // asc | desc
  const [viewMode, setViewMode] = useState('live');  // 'live' | 'recycle'
  // Iter182++ — right-click context menu state. `ctxMenu` is either
  // null or { x, y, template }. Rendered as a fixed-position popover.
  const [ctxMenu, setCtxMenu] = useState(null);
  // Iter182++ — multi-select for bulk soft-delete / restore / hard-delete.
  // `selectMode` flips the cards into checkbox mode; `selectedIds` is the
  // Set of currently-checked template ids. Reset when viewMode flips.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => { setSelectedIds(new Set()); }, [viewMode]);
  useEffect(() => { if (!selectMode) setSelectedIds(new Set()); }, [selectMode]);
  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Dismiss the context menu on Escape so it never traps the operator.
  useEffect(() => {
    if (!ctxMenu) return;
    const onKey = (e) => { if (e.key === 'Escape') setCtxMenu(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ctxMenu]);
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        page, limit: 60, search,
        sort_by: sortBy,
        sort_dir: sortDir,
        ...(viewMode === 'recycle' ? { only_deleted: 'true' } : {}),
      });
      const { data } = await api.get(`/roof-templates?${qs.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch { setItems([]); }
    setLoading(false);
  }, [api, page, search, sortBy, sortDir, viewMode]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setUploadFlash(null);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach(f => fd.append('files', f));
      const { data } = await api.post('/roof-templates/bulk-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadFlash({ ok: true, ...data });
      load();
    } catch (e) {
      setUploadFlash({ ok: false, error: e?.response?.data?.detail || 'Upload failed' });
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Drag-and-drop wiring on the drop zone
  useEffect(() => {
    const el = dropAreaRef.current;
    if (!el) return;
    const onDragOver = (e) => { e.preventDefault(); el.classList.add('ring-2', 'ring-[#D35400]'); };
    const onDragLeave = () => el.classList.remove('ring-2', 'ring-[#D35400]');
    const onDrop = (e) => {
      e.preventDefault();
      el.classList.remove('ring-2', 'ring-[#D35400]');
      if (isAdmin) handleUpload(e.dataTransfer.files);
    };
    el.addEventListener('dragover', onDragOver);
    el.addEventListener('dragleave', onDragLeave);
    el.addEventListener('drop', onDrop);
    return () => {
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('dragleave', onDragLeave);
      el.removeEventListener('drop', onDrop);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const saveEdit = async () => {
    if (!editForm) return;
    const { _id, name, notes, category, hip_count, ridge_count, valley_count } = editForm;
    await api.put(`/roof-templates/${_id}`, {
      name, notes, category,
      hip_count: parseInt(hip_count, 10) || 0,
      ridge_count: parseInt(ridge_count, 10) || 0,
      valley_count: parseInt(valley_count, 10) || 0,
    });
    setEditForm(null);
    setSelected(null);
    load();
  };

  const deleteTemplate = async (id) => {
    // Iter182++ — soft-delete by default; goes to the recycle bin and is
    // restorable. Use `permanentlyDeleteTemplate` for hard delete.
    if (!window.confirm('Move this template to the recycle bin?\n\n(You can restore it later from the Recycle Bin view.)')) return;
    try {
      await api.delete(`/roof-templates/${id}?mode=soft`);
      toast.success('Moved to recycle bin');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const restoreTemplate = async (id) => {
    try {
      await api.post(`/roof-templates/${id}/restore`);
      toast.success('Template restored');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  const permanentlyDeleteTemplate = async (id) => {
    if (!window.confirm('Permanently delete this template?\n\nThis cannot be undone.')) return;
    try {
      await api.delete(`/roof-templates/${id}?mode=hard`);
      toast.success('Template permanently deleted');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Iter182++ — bulk runner. Drives the right action per id in parallel
  // (the backend endpoints are idempotent and lock per-doc), then
  // reports a single rollup toast.
  const runBulk = async (mode) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const messages = {
      soft:    `Move ${ids.length} template${ids.length === 1 ? '' : 's'} to the recycle bin?`,
      restore: `Restore ${ids.length} template${ids.length === 1 ? '' : 's'} from the recycle bin?`,
      hard:    `Permanently delete ${ids.length} template${ids.length === 1 ? '' : 's'}?\n\nThis cannot be undone.`,
    };
    if (!window.confirm(messages[mode])) return;
    setBulkBusy(true);
    let ok = 0, failed = 0;
    await Promise.all(ids.map(async (id) => {
      try {
        if (mode === 'restore') {
          await api.post(`/roof-templates/${id}/restore`);
        } else {
          await api.delete(`/roof-templates/${id}?mode=${mode}`);
        }
        ok++;
      } catch (e) {
        failed++;
      }
    }));
    setBulkBusy(false);
    setSelectedIds(new Set());
    if (failed === 0) {
      const verb = mode === 'soft' ? 'Moved to recycle bin' : mode === 'restore' ? 'Restored' : 'Permanently deleted';
      toast.success(`${verb}: ${ok} template${ok === 1 ? '' : 's'}`);
    } else {
      toast.error(`Processed ${ok}, ${failed} failed`);
    }
    load();
  };

  const selectAllOnPage = () => {
    setSelectedIds(new Set(items.map(t => t._id)));
  };

  // Open the trace-picker: fetch every aerial in the library so the user
  // can pick one to trace. Aerials are the JPEG/photo-style entries
  // tagged with `aerial=true` on upload.
  const openTracePicker = async () => {
    setTracePickerOpen(true);
    setTraceAerialsLoading(true);
    try {
      const { data } = await api.get('/roof-templates', { params: { aerial_only: true, limit: 60 } });
      setTraceAerials(data?.items || []);
    } catch {
      setTraceAerials([]);
    } finally {
      setTraceAerialsLoading(false);
    }
  };

  return (
    <div data-testid="roof-templates-page" className="space-y-4">
      {/* Iter180 — context banner when arriving from a property's "Next:
          Choose Roof Template" CTA. Pins the screenshot + address so
          the operator can compare templates side-by-side and jump back. */}
      {ctxPropertyId && (
        <div data-testid="roof-templates-context-banner" className="flex items-center gap-4 bg-gradient-to-r from-[#4A6FA5]/10 to-transparent border border-[#4A6FA5]/30 rounded-lg p-3">
          <button
            data-testid="roof-templates-back-to-property"
            onClick={() => navigate(ctxReturnTo || `/properties/${ctxPropertyId}`)}
            className="flex items-center gap-1 text-xs font-semibold text-[#4A6FA5] hover:text-[#3a5a89] flex-shrink-0"
          ><ArrowLeft size={12} /> {ctxReturnTo ? 'Back to Runner' : 'Back to property'}</button>
          {ctxPhotoUrl ? (
            <img src={ctxPhotoUrl} alt="screenshot" className="w-20 h-14 object-cover rounded border border-white shadow-sm flex-shrink-0" />
          ) : (
            <div className="w-20 h-14 bg-slate-100 rounded border border-white flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={14} className="text-slate-300" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#4A6FA5]">Choosing template for</p>
            <p className="text-sm font-semibold text-slate-800 truncate">{ctxAddress || `Property ${ctxPropertyId.slice(-6)}`}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">Click any template to compare against the screenshot, or trace a new one from this aerial.</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Roof Design Templates</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            <span className="font-mono-num font-bold text-slate-700">{total.toLocaleString()}</span> {total === 1 ? 'template' : 'templates'} in the library
          </p>
          {/* Sprint 1 (2026-02c) — tagging progress. Untagged +
              partial cards need Re-Trace before they can appear on
              property + appraisal pickers (`status_filter=complete`
              filter · admin can still see them all here). */}
          {items.length > 0 && (() => {
            const c = items.reduce((a, t) => { a[t.status || 'untagged'] = (a[t.status || 'untagged'] || 0) + 1; return a; }, {});
            return (
              <div className="flex gap-2 mt-2 flex-wrap" data-testid="rtpl-status-summary">
                {c.complete > 0 && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ring-1 bg-emerald-100 text-emerald-800 ring-emerald-300">{c.complete} Complete</span>}
                {c.partial > 0 && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ring-1 bg-amber-100 text-amber-800 ring-amber-300">{c.partial} Partial</span>}
                {c.untagged > 0 && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ring-1 bg-rose-100 text-rose-800 ring-rose-300">{c.untagged} Untagged — hover a card + click "Create new" to trace it</span>}
              </div>
            );
          })()}
        </div>
        {isAdmin && (
          <button
            data-testid="rtpl-trace-btn"
            onClick={() => {
              if (compareMode && ctxPhotoUrl) {
                // Iter180 Phase 3 — when arriving from a property's
                // screenshot, skip the aerial picker and trace
                // directly on top of the pinned screenshot.
                setTracingAerial({
                  thumbnail_url: ctxPhotoUrl,
                  name: ctxAddress || 'Property Roof Screenshot',
                  property_id: ctxPropertyId,
                });
              } else {
                openTracePicker();
              }
            }}
            className="flex items-center gap-2 px-4 py-2 border border-[#0F4F1B] text-[#0F4F1B] hover:bg-[#0F4F1B] hover:text-white rounded-md text-sm font-semibold transition-all"
            title={compareMode ? 'Trace a new template on top of this property\'s Roof Screenshot.' : 'Pick a saved aerial and trace its outline to create a brand-new line-art template.'}
          >
            <PenTool size={16} strokeWidth={2.5} /> {compareMode ? 'Trace from this screenshot' : 'Trace new template'}
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="rtpl-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all disabled:opacity-50"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} strokeWidth={2.5} />}
            {uploading ? 'Uploading…' : 'Upload templates'}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".doc,.docx,.png,.jpg,.jpeg,.gif,.bmp,.webp"
          onChange={e => handleUpload(e.target.files)}
          className="hidden"
          data-testid="rtpl-file-input"
        />
      </div>

      {/* Upload flash */}
      {uploadFlash && (
        <div
          data-testid="rtpl-upload-flash"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold ${uploadFlash.ok ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-red-50 text-red-700 ring-1 ring-red-200'}`}
        >
          {uploadFlash.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
          {uploadFlash.ok ? (
            <>{uploadFlash.created} created · {uploadFlash.skipped} skipped (duplicates) · {uploadFlash.failed} failed</>
          ) : (
            <>Upload failed — {uploadFlash.error}</>
          )}
          <button onClick={() => setUploadFlash(null)} className="ml-auto opacity-60 hover:opacity-100"><X size={12} /></button>
        </div>
      )}

      {/* Iter180 Phase 2 — split-pane layout when arriving from a
          property's Roof Screenshot. Left column pins the screenshot
          (with a ghost-overlay of whichever template the operator
          clicks) and the right column is a scrollable template list. */}
      <div className={compareMode ? 'grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-5' : ''}>
        {/* LEFT: Compare panel (sticky) — only rendered in compareMode */}
        {compareMode && (
          <aside data-testid="rtpl-compare-panel" className="lg:sticky lg:top-4 self-start space-y-3">
            <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E2DF] bg-slate-50">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600 flex items-center gap-1.5">
                  <Layers size={12} className="text-[#4A6FA5]" /> Compare against screenshot
                </p>
                <div className="flex items-center gap-3">
                  {/* Iter180 Phase 3 — swap screenshot without
                      leaving the templates page. Opens the same
                      drop-zone + Google-Earth picker used on the
                      property page. */}
                  <button
                    data-testid="rtpl-change-screenshot"
                    onClick={() => setScreenshotPickerOpen(true)}
                    className="flex items-center gap-1 text-[10px] font-semibold text-[#4A6FA5] hover:text-[#3a5a89]"
                  ><RefreshCcw size={11} /> Change screenshot</button>
                  {overlay && (
                    <button
                      data-testid="rtpl-clear-overlay"
                      onClick={() => setOverlay(null)}
                      className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-rose-600"
                    ><Eraser size={11} /> Clear overlay</button>
                  )}
                </div>
              </div>
              <div className="relative bg-slate-100 flex items-center justify-center" style={{ minHeight: 280 }}>
                {ctxPhotoUrl ? (
                  <img src={ctxPhotoUrl} alt="Property roof screenshot" className="block w-full h-auto object-contain" />
                ) : (
                  <div className="text-xs text-slate-400 p-8 text-center">Screenshot not available.</div>
                )}
                {/* Ghost overlay — sits absolutely on top of the
                    screenshot, scaled to its bounds via object-contain
                    so the operator sees the template line-art through
                    the aerial. Opacity is slider-controlled. */}
                {overlay && (
                  <img
                    data-testid="rtpl-overlay-image"
                    src={overlay.thumbnail_url}
                    alt={`overlay: ${overlay.name}`}
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none mix-blend-multiply"
                    style={{ opacity: overlayOpacity }}
                  />
                )}
              </div>
              {overlay && (
                <div className="px-3 py-2 border-t border-[#E2E2DF] bg-white space-y-2">
                  <p className="text-xs font-semibold text-slate-800 truncate" title={overlay.name}>{overlay.name}</p>
                  {/* Iter180 Phase 3 — accept this template as the
                      property's canonical Roof Template and bounce
                      back to the property page. Placed ABOVE the
                      opacity slider so it's the first thing the
                      operator's eye lands on after picking. */}
                  <button
                    data-testid="rtpl-accept-main"
                    onClick={() => acceptOverlayAsMain()}
                    disabled={accepting || acceptedId === overlay._id}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-[#0F4F1B] hover:bg-[#0A3A14] text-white text-sm font-bold rounded-md shadow-sm hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {accepting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    {acceptedId === overlay._id
                      ? 'Already the main template'
                      : accepting ? 'Saving…' : 'Accept as Main Roof Template'}
                  </button>
                  {acceptedId && acceptedId !== overlay._id && (
                    <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 text-center">A different template is currently set as the main — accepting will replace it.</p>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <label className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide w-12 flex-shrink-0">Opacity</label>
                    <input
                      data-testid="rtpl-overlay-opacity"
                      type="range"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={overlayOpacity}
                      onChange={e => setOverlayOpacity(parseFloat(e.target.value))}
                      className="flex-1 accent-[#D35400]"
                    />
                    <span className="text-[10px] font-mono-num text-slate-500 w-9 text-right">{Math.round(overlayOpacity * 100)}%</span>
                  </div>
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-500">
              Click any template on the right to ghost-trace it over the screenshot. Adjust opacity to see the underlying roof through the template lines. Use <em>Trace new template</em> if nothing matches.
            </p>
          </aside>
        )}

        {/* RIGHT (or full-width when not in compareMode): search + grid */}
        <div className="space-y-4">
      {/* Search + Sort + Recycle-bin toggle */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="rtpl-search"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, e.g. '5 Hipper', 'Valley', 'Dog Leg'…"
            className="w-full pl-10 pr-4 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm focus:border-[#D35400] focus:outline-none"
          />
        </div>
        {/* Iter182++ — sort dropdown. Mirrors the backend whitelist. */}
        <select
          data-testid="rtpl-sort-by"
          value={`${sortBy}:${sortDir}`}
          onChange={e => {
            const [f, d] = e.target.value.split(':');
            setSortBy(f); setSortDir(d); setPage(1);
          }}
          className="px-3 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm focus:border-[#D35400] focus:outline-none"
          title="Sort templates"
        >
          <option value="name:asc">Name (A → Z)</option>
          <option value="name:desc">Name (Z → A)</option>
          <option value="created_at:desc">Newest first</option>
          <option value="created_at:asc">Oldest first</option>
          <option value="category:asc">Category (A → Z)</option>
          <option value="hip_count:desc">Most hips</option>
          <option value="ridge_count:desc">Most ridges</option>
          {viewMode === 'recycle' && <option value="deleted_at:desc">Recently deleted</option>}
        </select>
        {/* Iter182++ — recycle-bin toggle. Admin-only. */}
        {isAdmin && (
          <button
            data-testid="rtpl-recycle-toggle"
            type="button"
            onClick={() => { setViewMode(viewMode === 'recycle' ? 'live' : 'recycle'); setSelected(null); setPage(1); }}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
              viewMode === 'recycle'
                ? 'bg-[#D35400] text-white border border-[#A04000] hover:bg-[#A04000]'
                : 'bg-white border border-[#E2E2DF] text-slate-700 hover:border-[#D35400] hover:text-[#D35400]'
            }`}
            title={viewMode === 'recycle' ? 'Back to live templates' : 'View recycle bin'}
          >
            <Trash2 size={14} />
            {viewMode === 'recycle' ? 'Back to library' : 'Recycle bin'}
          </button>
        )}
        {/* Iter182++ — multi-select toggle. Flips cards into checkbox mode
            so the operator can sweep through duplicates and bulk-delete. */}
        {isAdmin && (
          <button
            data-testid="rtpl-select-toggle"
            type="button"
            onClick={() => setSelectMode(s => !s)}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
              selectMode
                ? 'bg-sky-600 text-white border border-sky-700 hover:bg-sky-700'
                : 'bg-white border border-[#E2E2DF] text-slate-700 hover:border-sky-500 hover:text-sky-600'
            }`}
            title={selectMode ? 'Exit multi-select' : 'Multi-select for bulk actions'}
          >
            <CheckCircle2 size={14} />
            {selectMode ? `${selectedIds.size} selected` : 'Select'}
          </button>
        )}
      </div>
      {/* Iter182++ — recycle-bin banner so the operator never confuses
          the bin with the live library. */}
      {viewMode === 'recycle' && (
        <div data-testid="rtpl-recycle-banner" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-xs flex items-center gap-2">
          <Trash2 size={13} />
          <span>
            <strong>Recycle bin</strong> — templates here are soft-deleted. Click a template to <em>Restore</em> or <em>Permanently delete</em>.
          </span>
        </div>
      )}

      {/* Grid */}
      <div
        ref={dropAreaRef}
        data-testid="rtpl-grid"
        className="border border-dashed border-[#E2E2DF] rounded-xl bg-white/40 p-4 transition-colors"
      >
        {isAdmin && (
          <p className="text-[11px] text-slate-400 text-center mb-3">
            <Upload size={11} className="inline -mt-0.5" /> Drag &amp; drop .doc / .docx / .png / .gif files anywhere on this grid to bulk-upload — duplicates by name are auto-skipped.
          </p>
        )}
        {loading ? (
          <div className="text-center py-16 text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm space-y-2">
            <FileImage size={32} className="mx-auto opacity-40" />
            <p>No templates {search ? 'match your search' : 'yet'}.</p>
            {isAdmin && !search && <p className="text-xs">Drop .doc files here or click <strong>Upload templates</strong> to get started.</p>}
          </div>
        ) : (
          <div className={compareMode
            ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3'
            : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3'}>
            {items.map(t => {
              const isOverlay = overlay?._id === t._id;
              return (
              <button
                key={t._id}
                data-testid={`rtpl-card-${t._id}`}
                onClick={(e) => {
                  if (selectMode) {
                    e.stopPropagation();
                    toggleSelected(t._id);
                    return;
                  }
                  if (compareMode) {
                    // Iter180 Phase 2 — clicking sets/toggles overlay
                    // rather than opening the detail modal. The
                    // detail modal can still be reached from the
                    // standalone /roof-templates view.
                    setOverlay(prev => (prev?._id === t._id ? null : t));
                  } else {
                    setSelected(t);
                  }
                }}
                onContextMenu={isAdmin ? (e) => {
                  // Iter182++ — right-click → context menu with quick
                  // Move-to-recycle / Restore / Permanently-delete
                  // actions, so the operator never has to open the
                  // detail modal to soft-delete duplicates.
                  e.preventDefault();
                  e.stopPropagation();
                  setCtxMenu({ x: e.clientX, y: e.clientY, template: t });
                } : undefined}
                className={`group relative bg-white border rounded-md overflow-hidden text-left hover:border-[#D35400] hover:shadow-md hover:shadow-orange-500/10 transition-all ${
                  selectedIds.has(t._id) ? 'border-sky-500 ring-2 ring-sky-400/50'
                  : isOverlay ? 'border-[#D35400] ring-2 ring-[#D35400]/40'
                  : 'border-[#E2E2DF]'}`}
              >
                {/* Iter182++ — multi-select checkbox overlay. Renders
                    only in selectMode; clicks bubble up to the card's
                    onClick which calls toggleSelected. */}
                {selectMode && (
                  <div
                    data-testid={`rtpl-check-${t._id}`}
                    className={`absolute top-1.5 left-1.5 z-10 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedIds.has(t._id)
                        ? 'bg-sky-600 border-sky-700'
                        : 'bg-white/90 border-slate-400'
                    }`}
                    aria-checked={selectedIds.has(t._id)}
                    role="checkbox"
                  >
                    {selectedIds.has(t._id) && <CheckCircle2 size={12} className="text-white" strokeWidth={3} />}
                  </div>
                )}
                <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center overflow-hidden">
                  <img
                    src={t.thumbnail_url}
                    alt={t.name}
                    loading="lazy"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                {isOverlay && (
                  <>
                    <div className="absolute top-1 right-1 bg-[#D35400] text-white text-[8px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5">Overlay</div>
                    {/* Iter180 Phase 3+ — quick-accept button right on
                        the card so the operator can confirm without
                        scrolling to the compare panel. */}
                    {compareMode && (
                      <button
                        data-testid={`rtpl-accept-card-${t._id}`}
                        onClick={(e) => { e.stopPropagation(); acceptOverlayAsMain(t); }}
                        disabled={accepting || acceptedId === t._id}
                        className="absolute bottom-1 right-1 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-1 shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed bg-[#0F4F1B] text-white hover:bg-[#0A3A14]"
                        title="Set this template as the property's main roof template"
                      >
                        {acceptedId === t._id ? (<><CheckCircle2 size={9} /> Main</>) : (<><CheckCircle2 size={9} /> Set as Main</>)}
                      </button>
                    )}
                  </>
                )}
                <div className="p-2.5 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-800 truncate" title={t.name}>{t.name}</p>
                  <div className="flex items-center gap-1.5 mt-1 text-[10px] text-slate-400 flex-wrap">
                    {(() => {
                      // Sprint 1++ — badges prefer trace-derived counts
                      // over filename-parsed ones (accurate once traced;
                      // falls back to legacy fields for untagged docs).
                      const dc = t.derived?.counts || {};
                      const hip     = dc.hip    || t.hip_count    || 0;
                      const ridge   = dc.ridge  || t.ridge_count  || 0;
                      const valley  = dc.valley || t.valley_count || 0;
                      const barge   = dc.barge  || t.barge_count  || 0;
                      return (
                        <>
                          {hip > 0    && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-semibold">{hip} Hip</span>}
                          {ridge > 0  && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded font-semibold">{ridge} Ridge</span>}
                          {valley > 0 && <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded font-semibold">{valley} Valley</span>}
                          {barge > 0  && <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded font-semibold">{barge} Barge</span>}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Sprint 1 (2026-02c) — Re-Trace status chip (top-right)
                    + hover-reveal Trace button so Trevor can batch-tag
                    the untagged templates without leaving the library. */}
                {t.status && (
                  <span
                    data-testid={`rtpl-status-${t._id}`}
                    className={`absolute top-1.5 left-1.5 z-10 text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 ring-1 pointer-events-none ${
                      t.status === 'complete' ? 'bg-emerald-100 text-emerald-800 ring-emerald-300' :
                      t.status === 'partial'  ? 'bg-amber-100 text-amber-800 ring-amber-300'
                                              : 'bg-rose-100 text-rose-800 ring-rose-300'
                    }`}
                  >
                    {t.status}
                  </span>
                )}
                {isAdmin && !selectMode && !compareMode && (
                  <div className="absolute top-1 right-1 z-10 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    {(t.strokes || []).some(s => s?.type !== 'point') && (
                      <span role="button" tabIndex={0} data-testid={`rtpl-edit-drawing-${t._id}`}
                        onClick={(e) => { e.stopPropagation(); setTracingAerial({ _id: t._id, name: t.name, thumbnail_url: t.thumbnail_url, _source: t, _mode: 'edit' }); }}
                        title="Edit this template's lines and pins"
                        className="text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-1 shadow-md bg-slate-700 text-white hover:bg-slate-900">Edit</span>
                    )}
                    <span role="button" tabIndex={0} data-testid={`rtpl-retrace-${t._id}`}
                      onClick={(e) => { e.stopPropagation(); setTracingAerial({ _id: t._id, name: t.name, thumbnail_url: t.thumbnail_url, _source: t, _mode: 'copy' }); }}
                      title="Create a new template from this one (the original is kept)"
                      className="text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-1 shadow-md bg-indigo-600 text-white hover:bg-indigo-700">Create new</span>
                  </div>
                )}
              </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-slate-400">Page {page} of {pages}</span>
          <div className="flex gap-1">
            <button data-testid="rtpl-prev" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-2 rounded border border-slate-200 disabled:opacity-30"><ChevronLeft size={14} /></button>
            <button data-testid="rtpl-next" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-2 rounded border border-slate-200 disabled:opacity-30"><ChevronRight size={14} /></button>
          </div>
        </div>
      )}
        </div>{/* /right column */}
      </div>{/* /split-pane */}

      {/* Iter182++ — floating bulk action bar. Shows when multi-select
          is active and at least one card is checked. Mode-aware: live
          view → "Move N to recycle bin"; recycle-bin view → "Restore N"
          + "Permanently delete N". */}
      {isAdmin && selectMode && selectedIds.size > 0 && (
        <div
          data-testid="rtpl-bulk-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-3 border border-slate-700"
        >
          <span className="text-sm font-semibold whitespace-nowrap">
            <span className="text-sky-300 font-bold">{selectedIds.size}</span> selected
          </span>
          <button
            type="button"
            onClick={selectAllOnPage}
            className="text-[11px] uppercase tracking-wide text-slate-300 hover:text-white px-2 py-0.5 rounded border border-slate-600 hover:border-slate-400"
            title="Select every template on this page"
          >Select all on page</button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-[11px] uppercase tracking-wide text-slate-300 hover:text-white px-2 py-0.5 rounded border border-slate-600 hover:border-slate-400"
            title="Clear selection"
          >Clear</button>
          <div className="w-px h-5 bg-slate-700" />
          {viewMode === 'recycle' ? (
            <>
              <button
                data-testid="rtpl-bulk-restore"
                type="button"
                disabled={bulkBusy}
                onClick={() => runBulk('restore')}
                className="flex items-center gap-1.5 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-3.5 py-1.5 disabled:opacity-50"
              >
                <RefreshCcw size={13} /> Restore {selectedIds.size}
              </button>
              <button
                data-testid="rtpl-bulk-hard-delete"
                type="button"
                disabled={bulkBusy}
                onClick={() => runBulk('hard')}
                className="flex items-center gap-1.5 text-sm font-bold bg-red-600 hover:bg-red-700 text-white rounded-full px-3.5 py-1.5 disabled:opacity-50"
              >
                <Trash2 size={13} /> Permanently delete {selectedIds.size}
              </button>
            </>
          ) : (
            <button
              data-testid="rtpl-bulk-soft-delete"
              type="button"
              disabled={bulkBusy}
              onClick={() => runBulk('soft')}
              className="flex items-center gap-1.5 text-sm font-bold bg-red-500 hover:bg-red-600 text-white rounded-full px-3.5 py-1.5 disabled:opacity-50"
            >
              <Trash2 size={13} /> Move {selectedIds.size} to recycle bin
            </button>
          )}
        </div>
      )}

      {/* Iter182++ — Right-click context menu. Floats at the click
          coords; clicking anywhere else dismisses via the backdrop. */}
      {ctxMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setCtxMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }}
          />
          <div
            data-testid="rtpl-context-menu"
            role="menu"
            className="fixed z-50 bg-white border border-slate-200 rounded-md shadow-xl py-1 min-w-[200px] text-sm"
            style={{
              left: `${Math.min(ctxMenu.x, window.innerWidth - 220)}px`,
              top:  `${Math.min(ctxMenu.y, window.innerHeight - 160)}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1.5 border-b border-slate-100 text-[11px] font-semibold text-slate-500 truncate" title={ctxMenu.template.name}>
              {ctxMenu.template.name}
            </div>
            {viewMode !== 'recycle' ? (
              <>
                <button
                  data-testid="rtpl-ctx-preview"
                  className="w-full text-left px-3 py-1.5 hover:bg-slate-100 flex items-center gap-2 text-slate-700"
                  onClick={() => { setSelected(ctxMenu.template); setCtxMenu(null); }}
                ><FileImage size={13} className="text-slate-400" /> Open / Preview</button>
                <button
                  data-testid="rtpl-ctx-edit"
                  className="w-full text-left px-3 py-1.5 hover:bg-slate-100 flex items-center gap-2 text-slate-700"
                  onClick={() => { setSelected(ctxMenu.template); setEditForm({ ...ctxMenu.template }); setCtxMenu(null); }}
                ><Edit2 size={13} className="text-slate-400" /> Edit details</button>
                <div className="border-t border-slate-100 my-1" />
                <button
                  data-testid="rtpl-ctx-recycle"
                  className="w-full text-left px-3 py-1.5 hover:bg-red-50 flex items-center gap-2 text-red-600 font-semibold"
                  onClick={() => { const tid = ctxMenu.template._id; setCtxMenu(null); deleteTemplate(tid); }}
                ><Trash2 size={13} /> Move to recycle bin</button>
              </>
            ) : (
              <>
                <button
                  data-testid="rtpl-ctx-restore"
                  className="w-full text-left px-3 py-1.5 hover:bg-emerald-50 flex items-center gap-2 text-emerald-700 font-semibold"
                  onClick={() => { const tid = ctxMenu.template._id; setCtxMenu(null); restoreTemplate(tid); }}
                ><RefreshCcw size={13} /> Restore</button>
                <button
                  data-testid="rtpl-ctx-hard-delete"
                  className="w-full text-left px-3 py-1.5 hover:bg-red-100 flex items-center gap-2 text-red-700 font-semibold"
                  onClick={() => { const tid = ctxMenu.template._id; setCtxMenu(null); permanentlyDeleteTemplate(tid); }}
                ><Trash2 size={13} /> Permanently delete</button>
              </>
            )}
          </div>
        </>
      )}

      {/* Detail / edit modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setSelected(null); setEditForm(null); }}>          <div data-testid="rtpl-modal" className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-slate-800 truncate">{editForm?.name || selected.name}</h3>
                <p className="text-[11px] text-slate-500">{selected.width} × {selected.height} px · uploaded by {selected.uploaded_by || '—'}</p>
              </div>
              <button onClick={() => { setSelected(null); setEditForm(null); }} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </header>

            <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-center min-h-[260px]">
                <img src={selected.thumbnail_url} alt={selected.name} className="max-w-full max-h-[480px] object-contain" />
              </div>

              <div className="space-y-3 text-xs">
                {/* Sprint 1++ (2026-02c) — Trace-derived breakdown.
                    Shows every pin kind the operator tagged (hip,
                    ridge, valley, barge, gutter, downpipe, flashing,
                    face, outlet, flue, pipe) with matching colours
                    from LINE_TYPES. In edit mode, exposes an "Apply
                    to details" button that copies the suggested name
                    + hip/ridge/valley counts into the form fields
                    so the operator doesn't have to hand-count. */}
                <TraceDerivedPanel
                  derived={selected.derived}
                  status={selected.status}
                  editing={!!editForm}
                  onApply={() => {
                    if (!editForm || !selected.derived) return;
                    const c = selected.derived.counts || {};
                    setEditForm({
                      ...editForm,
                      name: selected.derived.suggested_name || editForm.name,
                      hip_count: c.hip || 0,
                      ridge_count: c.ridge || 0,
                      valley_count: c.valley || 0,
                    });
                  }}
                />

                {editForm ? (
                  <>
                    <Field label="Name"><input data-testid="rtpl-edit-name" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs" /></Field>
                    <Field label="Category"><input value={editForm.category} onChange={e => setEditForm({ ...editForm, category: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs" /></Field>
                    <div className="grid grid-cols-3 gap-2">
                      <Field label="Hips"><input type="number" min={0} value={editForm.hip_count} onChange={e => setEditForm({ ...editForm, hip_count: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs font-mono-num" /></Field>
                      <Field label="Ridges"><input type="number" min={0} value={editForm.ridge_count} onChange={e => setEditForm({ ...editForm, ridge_count: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs font-mono-num" /></Field>
                      <Field label="Valleys"><input type="number" min={0} value={editForm.valley_count} onChange={e => setEditForm({ ...editForm, valley_count: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs font-mono-num" /></Field>
                    </div>
                    <Field label="Notes"><textarea data-testid="rtpl-edit-notes" rows={4} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs" /></Field>
                  </>
                ) : (
                  <>
                    <Info label="Source file" value={selected.source_filename} />
                    <Info label="Category" value={selected.category || '—'} />
                    <div className="flex gap-2">
                      <Info label="Hips" value={selected.hip_count || 0} compact />
                      <Info label="Ridges" value={selected.ridge_count || 0} compact />
                      <Info label="Valleys" value={selected.valley_count || 0} compact />
                    </div>
                    <Info label="Notes" value={selected.notes || '(none)'} multiline />
                    <TemplateUsageList api={api} templateId={selected._id} />
                  </>
                )}
              </div>
            </div>

            <footer className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
              <a
                data-testid="rtpl-open-image"
                href={selected.thumbnail_url}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-200 text-slate-700 rounded-md hover:bg-white"
              ><FileImage size={11} /> Open full size</a>
              {isAdmin && !editForm && viewMode !== 'recycle' && (
                <>
                  {/* Sprint 1 fix (2026-02c) — primary CTA when card
                      is opened: trace pins onto the template so it
                      graduates from Untagged → Complete. Same wiring
                      as the hover-reveal "Trace Now" chip on the
                      grid: sets tracingAerial with the retrace-source
                      marker so onSaved archives the original. */}
                  {(selected.strokes || []).some(s => s?.type !== 'point') && (
                    <button
                      data-testid="rtpl-edit-drawing-detail"
                      onClick={() => { setTracingAerial({ _id: selected._id, name: selected.name, thumbnail_url: selected.thumbnail_url, _source: selected, _mode: 'edit' }); setSelected(null); }}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md font-semibold bg-slate-700 text-white hover:bg-slate-900"
                      title="Edit this template's lines and pins (saves into this template)"
                    ><Edit2 size={11} /> Edit drawing</button>
                  )}
                  <button
                    data-testid="rtpl-trace-btn-detail"
                    onClick={() => {
                      setTracingAerial({
                        _id: selected._id,
                        name: selected.name,
                        thumbnail_url: selected.thumbnail_url,
                        _source: selected,
                        _mode: 'copy',
                      });
                      setSelected(null);
                    }}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                    title="Create a new template from this one (the original is kept)"
                  >
                    <Edit2 size={11} /> Create new from this
                  </button>
                  <button data-testid="rtpl-edit-btn" onClick={() => setEditForm({ ...selected })} className="flex items-center gap-1 text-xs px-3 py-1.5 border border-[#D35400] text-[#D35400] rounded-md hover:bg-orange-50"><Edit2 size={11} /> Edit details</button>
                  <button data-testid="rtpl-delete-btn" onClick={() => deleteTemplate(selected._id)} className="flex items-center gap-1 text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded-md hover:bg-red-50"><Trash2 size={11} /> Move to recycle bin</button>
                </>
              )}
              {isAdmin && !editForm && viewMode === 'recycle' && (
                <>
                  <button data-testid="rtpl-restore-btn" onClick={() => restoreTemplate(selected._id)} className="flex items-center gap-1 text-xs px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-md hover:bg-emerald-50 font-semibold"><RefreshCcw size={11} /> Restore</button>
                  <button data-testid="rtpl-hard-delete-btn" onClick={() => permanentlyDeleteTemplate(selected._id)} className="flex items-center gap-1 text-xs px-3 py-1.5 bg-red-600 text-white border border-red-700 rounded-md hover:bg-red-700 font-semibold"><Trash2 size={11} /> Permanently delete</button>
                </>
              )}
              {isAdmin && editForm && (
                <>
                  <button onClick={() => setEditForm(null)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-md">Cancel</button>
                  <button data-testid="rtpl-save-btn" onClick={saveEdit} className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md font-semibold"><Save size={11} /> Save</button>
                </>
              )}
            </footer>
          </div>
        </div>
      )}

      {/* Trace-picker — choose an aerial to trace */}
      {tracePickerOpen && (
        <div
          data-testid="rtpl-trace-picker"
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setTracePickerOpen(false)}
        >
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><PenTool size={14} className="text-[#0F4F1B]" /> Pick a source to trace</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  <strong>Aerials</strong> = property Google-Earth / drone screenshots ·
                  <strong className="ml-1">Untagged templates</strong> = library entries needing pins added.
                </p>
              </div>
              <button onClick={() => setTracePickerOpen(false)} className="p-1.5 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {/* Sprint 1 fix (2026-02c) — untagged templates section
                  so the operator can pick "trace onto an existing
                  template" from the same launcher rather than
                  hunting for the per-card hover button. Merges with
                  the aerials section beneath it. */}
              {items.filter(t => t.status !== 'complete').length > 0 && (
                <div className="mb-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Untagged / partial templates ({items.filter(t => t.status !== 'complete').length})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {items.filter(t => t.status !== 'complete').map(t => (
                      <button
                        key={`u-${t._id}`}
                        data-testid={`rtpl-trace-pick-untagged-${t._id}`}
                        onClick={() => {
                          setTracePickerOpen(false);
                          setTracingAerial({
                            _id: t._id,
                            name: t.name,
                            thumbnail_url: t.thumbnail_url,
                            _source: t,
                            _mode: 'copy',
                          });
                        }}
                        className="bg-white border border-rose-200 rounded-md overflow-hidden text-left hover:border-indigo-500 hover:shadow-md transition-all"
                      >
                        <div className="aspect-[4/3] bg-slate-50 overflow-hidden">
                          <img src={t.thumbnail_url} alt={t.name} loading="lazy" className="w-full h-full object-contain" />
                        </div>
                        <div className="p-2 border-t border-slate-100">
                          <p className="text-xs font-semibold text-slate-800 truncate" title={t.name}>{t.name}</p>
                          <p className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">
                            {t.status || 'untagged'}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-slate-100 my-4" />
                </div>
              )}
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                Aerials ({traceAerials.length})
              </p>
              {traceAerialsLoading ? (
                <div className="text-center py-10 text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading aerials…</div>
              ) : traceAerials.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm space-y-1">
                  <FileImage size={28} className="mx-auto opacity-40" />
                  <p>No aerials in the library yet.</p>
                  <p className="text-xs">Upload a Google Earth screenshot via an appraisal's Roof Diagrams card first.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {traceAerials.map(t => (
                    <button
                      key={t._id}
                      data-testid={`rtpl-trace-pick-${t._id}`}
                      onClick={() => { setTracePickerOpen(false); setTracingAerial(t); }}
                      className="bg-white border border-slate-200 rounded-md overflow-hidden text-left hover:border-[#0F4F1B] hover:shadow-md transition-all"
                    >
                      <div className="aspect-[4/3] bg-slate-50 overflow-hidden">
                        <img src={t.thumbnail_url} alt={t.name} loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-2 border-t border-slate-100">
                        <p className="text-xs font-semibold text-slate-800 truncate" title={t.name}>{t.name}</p>
                        <p className="text-[10px] text-slate-400">
                          {t.created_at ? new Date(t.created_at).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trace tool — actual drawing modal */}
      {tracingAerial && (
        <TraceTemplateModal
          open
          aerial={tracingAerial}
          sourceTemplate={tracingAerial._source || null}
          saveMode={tracingAerial._mode || 'new'}
          propertyId={tracingAerial.property_id || ''}
          api={api}
          defaultName={tracingAerial?.name ? `Traced — ${tracingAerial.name}` : 'Traced template'}
          onSaved={async (data) => {
            // Only a PICTURE template (no traced lines) is archived once its traced
            // replacement exists. Edit saves in place; Create-new keeps the original.
            const src = tracingAerial?._source;
            const srcIsPicture = src && !(src.strokes || []).some(s => s?.type !== 'point');
            if (src && srcIsPicture && tracingAerial._mode === 'copy' && data?._id && data._id !== src._id) {
              try {
                await api.delete(`/roof-templates/${src._id}?mode=soft`);
                toast.success(`Tagged "${src.name}" · original archived`);
              } catch (err) {
                toast.warning(`Tagged version saved · archive of original failed, resolve via right-click menu`);
              }
            }
            // Iter180 Phase 3 — refresh the list immediately and, if
            // we're in compareMode, auto-pre-select the brand-new
            // template as the overlay so the operator sees it
            // highlighted the moment the trace modal closes.
            await load();
            if (compareMode && data?._id) {
              setOverlay({
                _id: data._id,
                name: data.name,
                thumbnail_url: data.thumbnail_url || `/api/roof-templates/${data._id}/image`,
                hip_count: data.hip_count,
                ridge_count: data.ridge_count,
                valley_count: data.valley_count,
              });
            }
          }}
          onClose={() => { setTracingAerial(null); load(); }}
        />
      )}

      {/* Iter180 Phase 3 — swap-screenshot picker, opened from the
          compare panel header. Same component used on the property
          page (drop-zone + Google-Earth deep link + photo library)
          so the operator gets identical UX without leaving the
          templates page. On save we update activeScreenshotId which
          re-fetches the pinned thumbnail. */}
      {screenshotPickerOpen && compareMode && (
        <RoofDiagramPicker
          propertyId={ctxPropertyId}
          selectedId={activeScreenshotId}
          address={ctxAddress}
          onSelect={async (photoId) => {
            try {
              if (photoId) {
                // Persist the new pick on the property so the
                // property page reflects the swap when the operator
                // navigates back.
                await api.patch(`/properties/${ctxPropertyId}`, { roof_screenshot_photo_id: photoId });
              }
              setActiveScreenshotId(photoId || '');
              // Drop any current overlay so the operator sees the
              // fresh screenshot cleanly first.
              setOverlay(null);
            } catch (e) {
              alert(e?.response?.data?.detail || 'Failed to save screenshot');
            }
          }}
          onClose={() => setScreenshotPickerOpen(false)}
        />
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const Info = ({ label, value, multiline, compact }) => (
  <div className={compact ? 'flex-1' : ''}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
    <p className={`text-slate-800 ${multiline ? 'whitespace-pre-line' : 'truncate'} ${compact ? 'text-base font-bold font-mono-num' : ''}`}>{value}</p>
  </div>
);

// Sprint 1++ (2026-02c) — Trace-derived counts panel shown in the
// detail modal. Kept in sync with LINE_TYPES colours from
// TraceTemplateModal so the operator sees the same colour on the pin
// as on the badge here.
const PIN_COLOURS = {
  hip:      { label: 'Hip',      bg: 'bg-blue-50',    text: 'text-blue-800',    ring: 'ring-blue-200'    },
  ridge:    { label: 'Ridge',    bg: 'bg-sky-50',     text: 'text-sky-800',     ring: 'ring-sky-200'     },
  valley:   { label: 'Valley',   bg: 'bg-rose-50',    text: 'text-rose-800',    ring: 'ring-rose-200'    },
  barge:    { label: 'Barge',    bg: 'bg-amber-50',   text: 'text-amber-800',   ring: 'ring-amber-200'   },
  gutter:   { label: 'Gutter',   bg: 'bg-emerald-50', text: 'text-emerald-800', ring: 'ring-emerald-200' },
  downpipe: { label: 'Downpipe', bg: 'bg-yellow-50',  text: 'text-yellow-800',  ring: 'ring-yellow-200'  },
  flashing: { label: 'Flashing', bg: 'bg-slate-100',  text: 'text-slate-800',   ring: 'ring-slate-300'   },
  face:     { label: 'Face',     bg: 'bg-orange-50',  text: 'text-orange-800',  ring: 'ring-orange-200'  },
  outlet:   { label: 'Outlet',   bg: 'bg-yellow-50',  text: 'text-yellow-800',  ring: 'ring-yellow-200'  },
  flue:     { label: 'Flue',     bg: 'bg-cyan-50',    text: 'text-cyan-800',    ring: 'ring-cyan-200'    },
  pipe:     { label: 'Pipe',     bg: 'bg-cyan-50',    text: 'text-cyan-800',    ring: 'ring-cyan-200'    },
};
const PIN_ORDER = ['hip','ridge','valley','barge','gutter','downpipe','flashing','face','outlet','flue','pipe'];

const TraceDerivedPanel = ({ derived, status, editing, onApply }) => {
  const counts = derived?.counts || {};
  const suggested = derived?.suggested_name || '';
  const total = counts.total || 0;
  const perimeter = counts.perimeter_pins || 0;

  if (status === 'untagged' || total === 0) {
    return (
      <div data-testid="rtpl-trace-derived-empty" className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">From Trace</p>
        <p className="text-slate-500 italic">No pins tagged yet. Use "Trace Now" to add ridge / hip / valley / barge pins — accurate counts will appear here.</p>
      </div>
    );
  }
  return (
    <div data-testid="rtpl-trace-derived" className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
      <div className="flex items-baseline justify-between mb-1.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">From Trace <span className="font-mono-num text-slate-400 normal-case">· {total} pin{total === 1 ? '' : 's'}{perimeter > 0 ? ` · ${perimeter} on perimeter` : ''}</span></p>
        {editing && suggested && (
          <button
            type="button"
            data-testid="rtpl-derived-apply"
            onClick={onApply}
            className="text-[10px] font-bold uppercase tracking-wider text-[#D35400] hover:text-[#A04000] underline-offset-2 hover:underline"
            title="Copy the suggested name + hip / ridge / valley counts into the fields below"
          >Apply to details ▸</button>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {PIN_ORDER.filter(k => (counts[k] || 0) > 0).map(k => {
          const c = PIN_COLOURS[k];
          return (
            <span
              key={k}
              data-testid={`rtpl-derived-${k}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${c.bg} ${c.text} ${c.ring}`}
            >
              <span className="font-mono-num">{counts[k]}</span> {c.label}
            </span>
          );
        })}
      </div>
      {suggested && (
        <p data-testid="rtpl-derived-suggested" className="mt-1.5 text-[11px] text-slate-500">
          Suggested name: <span className="font-semibold text-slate-700">{suggested}</span>
        </p>
      )}
    </div>
  );
};
